import { useCallback, useEffect, useMemo, useRef } from 'react'

import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query'

import { type ThreadPage } from 'assistant/domain/Thread'

import { useAssistantServices } from '../AssistantServices'

export const THREADS_QUERY_KEY = ['assistant', 'threads'] as const

/** The agent writes the title in the background, a few seconds after the first answer. */
export const TITLE_POLL_INTERVAL_MS = 3000
export const TITLE_POLL_MAX_ATTEMPTS = 10

type ThreadsData = InfiniteData<ThreadPage, number>

export function useThreads() {
  const { threads: repository } = useAssistantServices()
  const queryClient = useQueryClient()
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Bumped whenever polling is stopped, so a tick that is still waiting on a
  // refetch can tell it belongs to a poll that is over.
  const pollGeneration = useRef(0)

  const query = useInfiniteQuery({
    queryKey: THREADS_QUERY_KEY,
    queryFn: ({ pageParam }) => repository.list(pageParam),
    // The agent numbers pages from 0. Page 1 is the second page.
    initialPageParam: 0,
    getNextPageParam: last => (last.hasMore ? last.page + 1 : undefined),
  })

  const threads = useMemo(() => query.data?.pages.flatMap(page => page.threads) ?? [], [query.data])

  const rename = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => repository.rename(id, title),
    onMutate: async ({ id, title }) => {
      await queryClient.cancelQueries({ queryKey: THREADS_QUERY_KEY })

      const previous = queryClient.getQueryData<ThreadsData>(THREADS_QUERY_KEY)

      queryClient.setQueryData<ThreadsData>(THREADS_QUERY_KEY, data =>
        data && {
          ...data,
          pages: data.pages.map(page => ({
            ...page,
            threads: page.threads.map(thread => (thread.id === id ? { ...thread, title } : thread)),
          })),
        }
      )

      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(THREADS_QUERY_KEY, context.previous)
    },
    // Only after a success. A failure is undone by the rollback above, with no
    // second request. Not returned: the rename is done when the server says so.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: THREADS_QUERY_KEY })
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => repository.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: THREADS_QUERY_KEY })
    },
  })

  const stopPolling = useCallback(() => {
    if (pollTimer.current) clearTimeout(pollTimer.current)
    pollTimer.current = null
    pollGeneration.current += 1
  }, [])

  useEffect(() => stopPolling, [stopPolling])

  /**
   * Refetches the list until the given thread is in it with a title, then
   * stops. It gives up after a few tries, so a title that never comes costs
   * nothing more. Meant for the end of the first answer of a new thread.
   */
  const refreshTitlesAfterFirstAnswer = useCallback(
    (threadId: string) => {
      stopPolling()

      const generation = pollGeneration.current
      let attempts = 0

      const tick = async () => {
        attempts += 1

        await queryClient.refetchQueries({ queryKey: THREADS_QUERY_KEY })

        if (generation !== pollGeneration.current) return

        const data = queryClient.getQueryData<ThreadsData>(THREADS_QUERY_KEY)
        const target = data?.pages.flatMap(page => page.threads).find(thread => thread.id === threadId)

        if (Boolean(target?.title.trim()) || attempts >= TITLE_POLL_MAX_ATTEMPTS) {
          pollTimer.current = null
          return
        }

        pollTimer.current = setTimeout(tick, TITLE_POLL_INTERVAL_MS)
      }

      pollTimer.current = setTimeout(tick, TITLE_POLL_INTERVAL_MS)
    },
    [queryClient, stopPolling]
  )

  return {
    threads,
    isLoading: query.isLoading,
    isError: query.isError,
    hasMore: Boolean(query.hasNextPage),
    refetch: () => query.refetch(),
    loadMore: () => query.fetchNextPage(),
    renameThread: (id: string, title: string) => rename.mutateAsync({ id, title }),
    deleteThread: (id: string) => remove.mutateAsync(id),
    refreshTitlesAfterFirstAnswer,
  }
}
